import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { MonitorHeartOutlined, Refresh } from "@mui/icons-material";
import { SERVICES_STATUS_CONFIG } from "../config/servicesStatus";
import { fetchAllServiceStatuses, STATUS_LEVELS } from "../utils/servicesStatusApi";

const AUTO_REFRESH_INTERVALS = [
  { label: "5s", value: 5000 },
  { label: "10s", value: 10000 },
  { label: "30s", value: 30000 },
  { label: "1m", value: 60000 },
];
const DEFAULT_REFRESH_INTERVAL_MS = 5000;

const STATUS_VISUALS = {
  [STATUS_LEVELS.HEALTHY]: {
    color: "#16a34a",
    bg: "rgba(240,253,244,0.9)",
    border: "rgba(22,163,74,0.30)",
    dot: "#16a34a",
  },
  [STATUS_LEVELS.DEGRADED]: {
    color: "#d97706",
    bg: "rgba(255,251,235,0.9)",
    border: "rgba(217,119,6,0.30)",
    dot: "#f59e0b",
  },
  [STATUS_LEVELS.UNHEALTHY]: {
    color: "#dc2626",
    bg: "rgba(254,242,242,0.9)",
    border: "rgba(220,38,38,0.30)",
    dot: "#dc2626",
  },
  [STATUS_LEVELS.UNKNOWN]: {
    color: "#6b7280",
    bg: "rgba(243,244,246,0.9)",
    border: "rgba(148,163,184,0.35)",
    dot: "#9ca3af",
  },
};

const getStatusVisual = (status) =>
  STATUS_VISUALS[status] || STATUS_VISUALS[STATUS_LEVELS.UNKNOWN];

const formatDuration = (value) =>
  typeof value === "number" && Number.isFinite(value)
    ? `${value.toFixed(2)} ms`
    : "—";

const formatTimestamp = (value) => {
  if (!value) return null;
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString();
  } catch {
    return null;
  }
};

const StatusPill = ({ status }) => {
  const visual = getStatusVisual(status);
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        px: 1.1,
        py: 0.4,
        borderRadius: "999px",
        backgroundColor: visual.bg,
        border: `1px solid ${visual.border}`,
        color: visual.color,
        fontWeight: 700,
        fontSize: "0.72rem",
        letterSpacing: "0.02em",
        whiteSpace: "nowrap",
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: visual.dot,
          flexShrink: 0,
        }}
      />
      {status}
    </Box>
  );
};

StatusPill.propTypes = {
  status: PropTypes.string.isRequired,
};

const DependencyRow = ({ dependency }) => {
  const visual = getStatusVisual(dependency.status);
  const row = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        py: 0.7,
        borderTop: "1px solid rgba(15, 23, 42, 0.06)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: visual.dot,
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{
            fontSize: "0.85rem",
            fontWeight: 600,
            color: "#374151",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {dependency.label}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontFamily: "'Roboto Mono', ui-monospace, monospace",
          fontSize: "0.75rem",
          color: dependency.status === STATUS_LEVELS.HEALTHY ? "#9ca3af" : visual.color,
          flexShrink: 0,
        }}
      >
        {formatDuration(dependency.durationMs)}
      </Typography>
    </Box>
  );

  if (dependency.description) {
    return (
      <Tooltip title={dependency.description} arrow placement="top">
        {row}
      </Tooltip>
    );
  }
  return row;
};

DependencyRow.propTypes = {
  dependency: PropTypes.shape({
    label: PropTypes.string,
    status: PropTypes.string,
    description: PropTypes.string,
    durationMs: PropTypes.number,
  }).isRequired,
};

const ServiceStatusCard = ({ result }) => {
  const visual = getStatusVisual(result.status);
  const timestamp = formatTimestamp(result.timestamp);

  const metaParts = [];
  if (typeof result.totalDurationMs === "number") {
    metaParts.push(formatDuration(result.totalDurationMs));
  }
  if (timestamp) metaParts.push(timestamp);

  const emptyDependencyLabel =
    result.note === "Liveness only" ? "Liveness only" : "No dependencies";

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: "24px",
        border: `1px solid ${visual.border}`,
        backgroundColor: visual.bg,
        boxShadow: "0 20px 44px rgba(236, 123, 55, 0.08)",
      }}
    >
      <CardContent
        sx={{
          p: { xs: 2, md: 2.5 },
          "&:last-child": { pb: { xs: 2, md: 2.5 } },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            mb: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "1.05rem",
                fontWeight: 800,
                color: "#202531",
                letterSpacing: "-0.01em",
                lineHeight: 1.2,
              }}
            >
              {result.name}
            </Typography>
            {result.note && (
              <Typography
                sx={{ fontSize: "0.72rem", color: "#9ca3af", fontWeight: 600 }}
              >
                {result.note}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
            {result.version && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 1,
                  py: 0.3,
                  borderRadius: "6px",
                  backgroundColor: "rgba(15, 23, 42, 0.06)",
                  border: "1px solid rgba(15, 23, 42, 0.12)",
                  fontFamily: "'Roboto Mono', ui-monospace, monospace",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: "#374151",
                  letterSpacing: "0.01em",
                }}
              >
                v{result.version}
              </Box>
            )}
            <Tooltip title={result.url} arrow placement="top">
              <Box>
                <StatusPill status={result.status} />
              </Box>
            </Tooltip>
          </Box>
        </Box>

        {!result.reachable && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.85,
              mb: 1.25,
              borderRadius: "12px",
              backgroundColor: "rgba(254, 242, 242, 0.9)",
              border: "1px solid rgba(220, 38, 38, 0.30)",
            }}
          >
            <Typography
              sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#dc2626" }}
            >
              Unreachable
            </Typography>
            {result.error && (
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  color: "#b91c1c",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                · {result.error}
              </Typography>
            )}
          </Box>
        )}

        {metaParts.length > 0 && (
          <Typography
            sx={{
              fontFamily: "'Roboto Mono', ui-monospace, monospace",
              fontSize: "0.72rem",
              color: "#6b7280",
              mb: 1,
              wordBreak: "break-word",
            }}
          >
            {metaParts.join("  ·  ")}
          </Typography>
        )}

        <Box sx={{ mt: 0.5 }}>
          {result.dependencies.length > 0 ? (
            result.dependencies.map((dependency) => (
              <DependencyRow key={dependency.name} dependency={dependency} />
            ))
          ) : (
            <Typography
              sx={{
                fontSize: "0.82rem",
                color: "#9ca3af",
                fontStyle: "italic",
                py: 0.7,
                borderTop: "1px solid rgba(15, 23, 42, 0.06)",
              }}
            >
              {emptyDependencyLabel}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

ServiceStatusCard.propTypes = {
  result: PropTypes.shape({
    name: PropTypes.string,
    url: PropTypes.string,
    note: PropTypes.string,
    reachable: PropTypes.bool,
    status: PropTypes.string,
    version: PropTypes.string,
    timestamp: PropTypes.string,
    totalDurationMs: PropTypes.number,
    error: PropTypes.string,
    dependencies: PropTypes.array,
  }).isRequired,
};

const ServicesStatusTab = () => {
  const [results, setResults] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(DEFAULT_REFRESH_INTERVAL_MS);

  const loadStatuses = useCallback(async () => {
    setRefreshing(true);
    try {
      const data = await fetchAllServiceStatuses(SERVICES_STATUS_CONFIG);
      setResults(data);
      setLastUpdated(new Date());
    } finally {
      setRefreshing(false);
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const intervalId = setInterval(loadStatuses, refreshInterval);
    return () => clearInterval(intervalId);
  }, [autoRefresh, refreshInterval, loadStatuses]);

  const total = results.length;
  const healthyCount = useMemo(
    () =>
      results.filter(
        (result) => result.reachable && result.status === STATUS_LEVELS.HEALTHY
      ).length,
    [results]
  );

  const summaryStatus = useMemo(() => {
    if (!results.length) return STATUS_LEVELS.UNKNOWN;
    if (
      results.some(
        (result) =>
          !result.reachable || result.status === STATUS_LEVELS.UNHEALTHY
      )
    ) {
      return STATUS_LEVELS.UNHEALTHY;
    }
    if (results.some((result) => result.status !== STATUS_LEVELS.HEALTHY)) {
      return STATUS_LEVELS.DEGRADED;
    }
    return STATUS_LEVELS.HEALTHY;
  }, [results]);

  const summaryVisual = getStatusVisual(summaryStatus);

  return (
    <Box
      sx={{
        maxWidth: 1400,
        mx: "auto",
        px: { xs: 1, md: 2 },
        py: { xs: 1, md: 2 },
      }}
    >
      <Box
        sx={{
          borderRadius: { xs: 4, md: 6 },
          background:
            "radial-gradient(circle at top center, rgba(255, 191, 154, 0.28), rgba(255,255,255,0.94) 42%), linear-gradient(180deg, rgba(255, 248, 243, 0.96), rgba(255, 255, 255, 0.92))",
          boxShadow: "0 24px 60px rgba(236, 123, 55, 0.10)",
          border: "1px solid rgba(255, 132, 76, 0.10)",
          p: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2.25,
            mb: 3.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2.25 }}>
            <Box
              sx={{
                width: { xs: 56, md: 64 },
                height: { xs: 56, md: 64 },
                borderRadius: "20px",
                background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
                boxShadow: "0 18px 38px rgba(255, 139, 71, 0.30)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                flexShrink: 0,
              }}
            >
              <MonitorHeartOutlined sx={{ fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                component="h2"
                variant="h5"
                sx={{
                  fontSize: { xs: "1.5rem", md: "1.8rem" },
                  lineHeight: 1.05,
                  fontWeight: 800,
                  color: "#202531",
                  letterSpacing: "-0.03em",
                  mb: 0.8,
                }}
              >
                Service status
              </Typography>
              <Typography variant="body1" sx={{ color: "#6b7280", fontWeight: 500 }}>
                Live health of core backend services and their dependencies.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            {!initialLoading && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.5,
                  py: 0.6,
                  borderRadius: "999px",
                  backgroundColor: summaryVisual.bg,
                  border: `1px solid ${summaryVisual.border}`,
                  color: summaryVisual.color,
                  fontWeight: 700,
                  fontSize: "0.85rem",
                }}
              >
                <Box
                  sx={{
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    backgroundColor: summaryVisual.dot,
                  }}
                />
                {healthyCount} / {total} healthy
              </Box>
            )}

            {lastUpdated && (
              <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                Last updated {lastUpdated.toLocaleTimeString()}
              </Typography>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={autoRefresh}
                  onChange={(event) => setAutoRefresh(event.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#f97316" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#f97316",
                    },
                  }}
                />
              }
              label="Auto-refresh"
              sx={{
                m: 0,
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: "#4b5563",
                },
              }}
            />

            {autoRefresh && (
              <Select
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(e.target.value)}
                size="small"
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: "#4b5563",
                  borderRadius: "10px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#d1d5db",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#f97316",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#f97316",
                  },
                }}
              >
                {AUTO_REFRESH_INTERVALS.map(({ label, value }) => (
                  <MenuItem key={value} value={value} sx={{ fontSize: "0.85rem" }}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            )}

            <Button
              variant="contained"
              onClick={loadStatuses}
              disabled={refreshing}
              startIcon={
                refreshing ? (
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                ) : (
                  <Refresh />
                )
              }
              sx={{
                minHeight: 44,
                px: 2.5,
                borderRadius: "16px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.9rem",
                color: "#fff",
                whiteSpace: "nowrap",
                background: "linear-gradient(180deg, #ff8a3d 0%, #ff6d1b 100%)",
                boxShadow: "0 14px 30px rgba(255, 109, 27, 0.28)",
                "&:hover": {
                  background: "linear-gradient(180deg, #ff7a24 0%, #f2610f 100%)",
                },
                "&.Mui-disabled": {
                  background: "rgba(255, 138, 61, 0.35)",
                  color: "#fff",
                  boxShadow: "none",
                },
              }}
            >
              {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
          </Box>
        </Box>

        {initialLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: "#f97316" }} />
            <Typography variant="body2" sx={{ color: "#6b7280" }}>
              Loading service status…
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
              },
              gap: 2.5,
            }}
          >
            {results.map((result) => (
              <ServiceStatusCard key={result.key} result={result} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ServicesStatusTab;
